// SEO Component for dynamic page-level meta tags
// Uses react-helmet-async for SSR-compatible head management

import { Helmet } from 'react-helmet-async';
import { organization } from '../config';

const SITE_URL = 'https://nsctalbotmd.org';
const DEFAULT_DESCRIPTION = 'The Neighborhood Service Center, Inc. is a non-profit Community Action Agency that provides services and assistance to low-income families and elderly in Talbot County, Maryland.';
const DEFAULT_IMAGE = `${SITE_URL}/logo512.png`;

/**
 * SEO Component for managing page-level meta tags
 * 
 * @param {Object} props
 * @param {string} props.title - Page title (will be appended with org name)
 * @param {string} props.description - Page meta description
 * @param {string} props.path - URL path (e.g., '/about')
 * @param {string} props.image - Open Graph image URL
 * @param {string} props.type - Open Graph type (default: 'website')
 * @param {Object} props.structuredData - Additional JSON-LD structured data
 */
export function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '',
  image = DEFAULT_IMAGE,
  type = 'website',
  structuredData = null
}) {
  const fullTitle = title 
    ? `${title} | ${organization.name}`
    : `${organization.name}: Homepage`;
  
  const canonicalUrl = `${SITE_URL}${path}`;
  const imageUrl = image.startsWith('http') ? image : `${SITE_URL}${image}`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:site_name" content={organization.name} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      
      {/* Additional Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}

/**
 * Generate WebPage structured data
 */
export function getWebPageStructuredData({ title, description, path }) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": title,
    "description": description,
    "url": `${SITE_URL}${path}`,
    "isPartOf": {
      "@type": "WebSite",
      "name": organization.name,
      "url": SITE_URL
    }
  };
}

/**
 * Generate BreadcrumbList structured data
 */
export function getBreadcrumbStructuredData(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `${SITE_URL}${item.path}`
    }))
  };
}

export default SEO;
